import React from "react";
import moment from "moment";
import qs from "qs";
import { useImmer } from "use-immer";
import { errorMessage } from "../../../utils";
import { getOrder } from "../../Orders/api";

const DATE_FORMAT = "YYYY-MM-DD";

const defaultPagination = {
  pageIndex: 1,
  pageSize: 50,
  pageCount: 0,
  total: 0,
  hasMorePages: true,
  lastPage: 1,
};

// An FTI can only be issued within the request period, which counts back from today:
// 2 days means the 2 days before today plus today. Orders before this date are not eligible.
const getRequestPeriodStartDate = (requestPeriodDays) =>
  requestPeriodDays === null
    ? null
    : moment()
        .subtract(Math.max(requestPeriodDays, 0), "days")
        .format(DATE_FORMAT);

// `requestPeriodDays` is the saved FTI setting, already loaded by `useFullTaxInvoice`
export const useFtiOrderList = (requestPeriodDays = null) => {
  const [state, setState] = useImmer({
    orders: [],
    pagination: defaultPagination,
    isLoading: true,
    search: "",
    date: null,
  });

  // ================================================================ API SECTION ======================================================
  const getOrders = async (currentState) => {
    triggerTableLoading(true);
    try {
      const response = await getOrder(
        {
          params: getRequestParams(currentState),
          paramsSerializer: (params) => qs.stringify(params),
        },
        currentState.pagination
      );
      const { success, message, data } = response;
      if (success) {
        setState((draft) => {
          draft.orders = data.orders;
          draft.pagination = data.pagination;
        });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message = error.response?.data?.message ?? "Failed to fetch orders";
      errorMessage(message);
    } finally {
      triggerTableLoading(false);
    }
  };

  //   ============================================================== UTIL FUNCTIONS ==============================================================
  const triggerTableLoading = (isLoading) => {
    setState((draft) => {
      draft.isLoading = isLoading;
    });
  };

  // Pagination is added to the url by `getOrder`
  const getRequestParams = ({ search, date }) => {
    const params = {};

    if (search) params.search = search;

    // A selected date narrows the list to that day, otherwise the whole request period is listed
    const startDate = date ?? getRequestPeriodStartDate(requestPeriodDays);
    if (startDate) {
      params.start_date = startDate;
      params.end_date = date ?? moment().format(DATE_FORMAT);
    }

    return params;
  };

  // Util function to handle navigation between pages of the table
  const gotoPage = (pageIndex) => {
    setState((draft) => {
      draft.pagination.pageIndex = pageIndex;
    });

    getOrders({ ...state, pagination: { ...state.pagination, pageIndex } });
  };

  // Util function to handle table page-size changes
  const setPageSize = (e) => {
    const pageSize = +e.target.value;
    const pageIndex = 1;

    setState((draft) => {
      draft.pagination.pageIndex = pageIndex;
      draft.pagination.pageSize = pageSize;
    });

    getOrders({
      ...state,
      pagination: { ...state.pagination, pageIndex, pageSize },
    });
  };

  // Handles the search and date filters, both refetch from the first page
  const handleFilterChange = (name, value) => {
    const pagination = { ...state.pagination, pageIndex: 1 };

    setState((draft) => {
      draft[name] = value;
      draft.pagination = pagination;
    });

    getOrders({ ...state, pagination, [name]: value });
  };

  //   ================================================================ USE EFFECTS =================================================================
  React.useEffect(() => {
    getOrders(state);
  }, []);

  return {
    state,
    minDate: getRequestPeriodStartDate(requestPeriodDays),
    gotoPage,
    setPageSize,
    handleFilterChange,
  };
};
